import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

const CustomCursor = () => {
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
    const [isHovering, setIsHovering] = useState(false);
    const [isClicking, setIsClicking] = useState(false);
    const [trail, setTrail] = useState([]);
    const [isDesktop, setIsDesktop] = useState(false);

    useEffect(() => {
        // Check if device has a mouse (desktop)
        const checkDesktop = () => {
            setIsDesktop(window.matchMedia('(pointer: fine)').matches && window.innerWidth > 768);
        };
        
        checkDesktop();
        window.addEventListener('resize', checkDesktop);

        if (!isDesktop) return; // Don't show custom cursor on mobile

        const updateMousePosition = (e) => {
            setMousePosition({ x: e.clientX, y: e.clientY });
            
            // Add to trail
            setTrail(prev => {
                const newTrail = [...prev, { x: e.clientX, y: e.clientY, id: Date.now() }];
                return newTrail.slice(-8); // Keep last 8 trail points
            });
        };

        const handleMouseDown = () => setIsClicking(true);
        const handleMouseUp = () => setIsClicking(false);

        // Check for hoverable elements
        const handleMouseOver = (e) => {
            const target = e.target;
            const isInteractive = 
                target.tagName === 'A' ||
                target.tagName === 'BUTTON' ||
                target.tagName === 'INPUT' ||
                target.tagName === 'TEXTAREA' ||
                target.closest('a') ||
                target.closest('button') ||
                target.closest('.project-card') ||
                target.closest('.skill-card') ||
                target.closest('.tech-badge') ||
                target.closest('.filter-btn') ||
                target.closest('.nav-link') ||
                target.closest('.timeline-card') ||
                target.closest('.btn') ||
                target.closest('[role="button"]') ||
                target.style.cursor === 'pointer' ||
                window.getComputedStyle(target).cursor === 'pointer';
            
            if (isInteractive) {
                setIsHovering(true);
            }
        };

        const handleMouseOut = (e) => {
            // Only set to false if we're not entering another interactive element
            if (!e.relatedTarget || 
                (e.relatedTarget.tagName !== 'A' && 
                 e.relatedTarget.tagName !== 'BUTTON' &&
                 !e.relatedTarget.closest('a') &&
                 !e.relatedTarget.closest('button'))) {
                setIsHovering(false);
            }
        };

        window.addEventListener('mousemove', updateMousePosition);
        window.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('mouseover', handleMouseOver);
        document.addEventListener('mouseout', handleMouseOut);

        return () => {
            window.removeEventListener('resize', checkDesktop);
            window.removeEventListener('mousemove', updateMousePosition);
            window.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('mouseover', handleMouseOver);
            document.removeEventListener('mouseout', handleMouseOut);
        };
    }, [isDesktop]);

    if (!isDesktop) {
        return null; // Don't render on mobile
    }

    return (
        <>
            {/* Hide default cursor only on desktop */}
            <style>{`
                @media (pointer: fine) and (min-width: 769px) {
                    * {
                        cursor: none !important;
                    }
                }
            `}</style>

            {/* Cursor Trail */}
            {trail.map((point, index) => (
                <motion.div
                    key={point.id}
                    className="cursor-trail"
                    initial={{ opacity: 0.3, scale: 1 }}
                    animate={{
                        x: point.x,
                        y: point.y,
                        opacity: 0,
                        scale: 0.5,
                    }}
                    transition={{
                        duration: 0.5,
                        ease: 'easeOut',
                    }}
                    style={{
                        position: 'fixed',
                        left: 0,
                        top: 0,
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.5) 0%, rgba(139, 92, 246, 0.3) 100%)',
                        pointerEvents: 'none',
                        zIndex: 9998,
                        transform: 'translate(-50%, -50%)',
                    }}
                />
            ))}

            {/* Main Cursor */}
            <motion.div
                className="custom-cursor"
                animate={{
                    x: mousePosition.x,
                    y: mousePosition.y,
                    scale: isHovering ? 1.5 : isClicking ? 0.8 : 1,
                }}
                transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 28,
                    mass: 0.5,
                }}
                style={{
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    background: isHovering
                        ? 'radial-gradient(circle, rgba(99, 102, 241, 0.9) 0%, rgba(139, 92, 246, 0.7) 100%)'
                        : 'radial-gradient(circle, rgba(99, 102, 241, 0.7) 0%, rgba(139, 92, 246, 0.5) 100%)',
                    border: isHovering ? '2px solid rgba(165, 180, 252, 0.9)' : '2px solid rgba(99, 102, 241, 0.6)',
                    pointerEvents: 'none',
                    zIndex: 9999,
                    transform: 'translate(-50%, -50%)',
                    boxShadow: isHovering
                        ? '0 0 25px rgba(99, 102, 241, 0.9), 0 0 50px rgba(139, 92, 246, 0.7)'
                        : '0 0 15px rgba(99, 102, 241, 0.6)',
                    mixBlendMode: 'normal',
                }}
            />

            {/* Outer Ring */}
            <motion.div
                className="cursor-ring"
                animate={{
                    x: mousePosition.x,
                    y: mousePosition.y,
                    scale: isHovering ? 2 : isClicking ? 0.5 : 1,
                    opacity: isHovering ? 0.3 : 0.5,
                }}
                transition={{
                    type: 'spring',
                    stiffness: 200,
                    damping: 20,
                }}
                style={{
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    border: '2px solid rgba(99, 102, 241, 0.4)',
                    pointerEvents: 'none',
                    zIndex: 9997,
                    transform: 'translate(-50%, -50%)',
                }}
            />

            {/* Glow Effect */}
            {isHovering && (
                <motion.div
                    className="cursor-glow"
                    animate={{
                        x: mousePosition.x,
                        y: mousePosition.y,
                        scale: [1, 1.5, 1],
                    }}
                    transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                    }}
                    style={{
                        position: 'fixed',
                        left: 0,
                        top: 0,
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.3) 0%, transparent 70%)',
                        pointerEvents: 'none',
                        zIndex: 9996,
                        transform: 'translate(-50%, -50%)',
                    }}
                />
            )}
        </>
    );
};

export default CustomCursor;

